import { useEffectOnce } from 'usehooks-ts';

import { ROUTE_P1_PRODUCT_SELECTION } from '../../../constants';
import useFormSessionVariants from '../../../hooks/useFormSessionVariants';
import useNavigateBookingEngine from '../../../hooks/useNavigateBookingEngine';
import useWritePreCart from '../../../hooks/useWritePreCart';
import { isArrEmpty } from '../../../utils/arrayUtil';
import FetchLoaderSpinner from '../../generic/FetchLoaderSpinner';
import ContainerLeft from '../../generic/layout/ContainerLeft';
import FlexRow from '../../generic/layout/FlexRow';
import ButtonAddSameSession from './ButtonAddSameSession';
import SessionVariantsBloc from './display/SessionVariantsBloc';
import SessionVariantsTotal from './SessionVariantsTotal';

const SessionVariantsForm = () => {
  const { preCartData, addPreCartSameSession, removePreCartSameSession } = useWritePreCart();
  const { product, availabilities, canUseSameSessionShortcut } = preCartData;

  const {
    onSubmitAddToCart,
    setRequestedAction,
    statusLoadVariants,
    statusAddCart,
    requestedAction,
    itemsToDeleteForEdit,
    invalidResources,
    duplicateParticipants,
    participantAlreadyInCart,
    maxAvailablePlaces,
    canBookMore,
    maxBookableSessions,
    displayVariantsAsQuantity,
  } = useFormSessionVariants(preCartData, availabilities, product);

  const navigate = useNavigateBookingEngine();
  useEffectOnce(() => {
    if (isArrEmpty(availabilities)) {
      navigate(ROUTE_P1_PRODUCT_SELECTION);
    }
  });

  if (!product || isArrEmpty(availabilities)) return <FetchLoaderSpinner />;

  const commonPropsWithTotal = {
    product: product,
    statusLoadVariants: statusLoadVariants,
    canBookMore: canBookMore,
  };

  const commonPropsForVariantDisplay = {
    nbAvaSelected: availabilities.length,
    defaultVariant: preCartData.defaultVariant,
    maxAvailablePlaces: maxAvailablePlaces,
    ...commonPropsWithTotal,
  };

  const commonPropsForVariantsBloc = {
    product: product,
    availabilities: availabilities,
    displayVariantsAsQuantity: displayVariantsAsQuantity,
    commonPropsForVariantDisplay: commonPropsForVariantDisplay,
  };

  return (
    <form onSubmit={onSubmitAddToCart}>
      <FlexRow>
        <ContainerLeft>
          {!canUseSameSessionShortcut && <SessionVariantsBloc {...commonPropsForVariantsBloc} availability={availabilities[0]} />}

          {canUseSameSessionShortcut && (
            <>
              {availabilities.map((ava, index) => (
                <SessionVariantsBloc
                  key={ava._key || index}
                  {...commonPropsForVariantsBloc}
                  availability={ava}
                  sameSessionIndex={index}
                  onRemoveSameSession={() => removePreCartSameSession(index)}
                />
              ))}

              {canBookMore && (
                <ButtonAddSameSession
                  onClick={addPreCartSameSession}
                  isDisabled={requestedAction.current != null || availabilities.length >= maxBookableSessions}
                  productTitle={product.title}
                />
              )}
            </>
          )}
        </ContainerLeft>

        <SessionVariantsTotal
          availabilities={availabilities}
          requestedAction={requestedAction}
          statusAddCart={statusAddCart}
          invalidResources={invalidResources}
          duplicateParticipants={duplicateParticipants}
          participantAlreadyInCart={participantAlreadyInCart}
          itemsToDeleteForEdit={itemsToDeleteForEdit}
          setRequestedAction={setRequestedAction}
          canUseSameSessionShortcut={canUseSameSessionShortcut}
          {...commonPropsWithTotal}
        />
      </FlexRow>
    </form>
  );
};

export default SessionVariantsForm;
